<template>
  <section id="new-features" class="services-section">
    <div class="section-header">
      
      <h2 class="section-title">
        <span>{{ t('services.title') }}</span>
      </h2>
      <p class="section-subtitle">
        {{ t('services.subtitle') }}
      </p>
    </div>

    <div class="services-hero glass-panel">
      
      <h3 class="section-header">{{ t('hero.description') }}</h3>
      
    </div>

    <div class="services-grid">
      <router-link
        v-for="(feature, index) in features"
        :key="feature.id"
        :to="{ name: 'service-detail', params: { selectedFeature: feature.id } }"
        class="service-card group"
      >
        <div class="service-media">
          <img
            :src="feature.main_image || fallbackImage"
            loading="lazy"
            :alt="feature.name"
          />
          <div class="service-media__overlay"></div>
          <span class="service-index">
            {{ (index + 1).toString().padStart(2, '0') }}
          </span>
        </div>
        <div class="service-card__body">
          <h3>{{ feature.name }}</h3>
          <p>{{ getDescription(feature) }}</p>
          <span class="service-card__cta">
            {{ t('buttons.learnMore') }}
            <svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 6H19M19 6L13 1M19 6L13 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </div>
      </router-link>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, tm, locale } = useI18n();
const fallbackImage = new URL('@/assets/section3.webp', import.meta.url).href;

// دالة لجلب مسار الصورة الرئيسية
const getMainImageUrl = (id) => {
  return new URL(`../assets/Backend/services/service${id}/main.png`, import.meta.url).href;
};

// جلب البيانات من ملفات الترجمة
const features = computed(() => {
  const data = tm('servicesData');
  if (Array.isArray(data)) {
    return data.map(feature => ({
      ...feature,
      main_image: getMainImageUrl(feature.id)
    }));
  }
  return [];
});

const getDescription = (feature) => {
  return feature.short_description || feature.description || '';
};
</script>

<style scoped>
.services-section {
  padding: 6rem 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.services-hero {
  margin: 0 auto 2.5rem;
  padding: 2rem;
  text-align: left;
  display: grid;
  gap: 0.75rem;
}

.services-hero h3 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--brand-secondary);
}

.services-hero p {
  margin: 0;
  color: var(--brand-muted);
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.75rem;
}

.service-card {
  display: flex;
  flex-direction: column;
  border-radius: 28px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(253, 143, 66, 0.12);
  box-shadow: var(--brand-shadow);
  transition: transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
}

.service-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 40px 110px rgba(0, 0, 0, 0.12);
  border-color: rgba(253, 143, 66, 0.4);
}

.service-media {
  position: relative;
  overflow: hidden;
  height: 220px;
}

.service-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.service-card:hover .service-media img {
  transform: scale(1.08);
}

.service-media__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.55) 100%);
}

.service-index {
  position: absolute;
  bottom: 18px;
  right: 20px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
}

.service-card__body {
  padding: 1.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.service-card__body h3 {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--brand-secondary);
}

.service-card__body p {
  color: rgba(31, 31, 31, 0.65);
  font-size: 0.98rem;
  min-height: 50px;
}

.service-card__cta {
  margin-top: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--brand-primary);
}

.dark .services-hero {
  background: rgba(8, 10, 16, 0.7);
  color: #fff;
}

.dark .service-card {
  background: rgba(15, 23, 42, 0.85);
  border-color: rgba(253, 143, 66, 0.2);
}

.dark .service-card__body h3,
.dark .service-card__body p {
  color: rgba(255, 255, 255, 0.85);
}
</style>
